import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';

// Material UI components
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Toolbar,
  Tooltip,
  Typography
} from '@mui/material';

// Material UI icons
import {
  Add as AddIcon,
  Edit as EditIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Link as LinkIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';

// Services
import { getSupplierParts } from '../../services/supplierService';

// Row component for expandable table
const SupplierRow = ({ supplier, onPartsLoad }) => {
  const [open, setOpen] = useState(false);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const handleToggleRow = async () => {
    const newOpen = !open;
    setOpen(newOpen);

    // Load parts data if expanding and not already loaded
    if (newOpen && !loaded) {
      setLoading(true);
      try {
        const partsData = await getSupplierParts(supplier.id);
        setParts(partsData);
        setLoaded(true);
        if (onPartsLoad) {
          onPartsLoad(supplier.id, partsData);
        }
      } catch (error) {
        console.error('Error loading supplier parts:', error);
        toast.error('Failed to load supplier parts');
        setParts([]);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      {/* Main supplier row */}
      <TableRow
        hover
        sx={{
          '& > *': { borderBottom: 'unset' },
          cursor: 'pointer'
        }}
        onClick={handleToggleRow}
      >
        <TableCell padding="checkbox">
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleToggleRow();
            }}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          {supplier.name}
        </TableCell>
        <TableCell>{supplier.contact_person || 'Not specified'}</TableCell>
        <TableCell>{supplier.email || 'Not specified'}</TableCell>
        <TableCell>{supplier.phone || 'Not specified'}</TableCell>
        <TableCell align="center">
          <Chip
            label={`${supplier.parts_count || 0} Parts`}
            color="primary"
            size="small"
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              if (!open) handleToggleRow();
            }}
          />
        </TableCell>
        <TableCell align="right">
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip title="View Details">
              <IconButton
                size="small"
                component={RouterLink}
                to={`/suppliers/${supplier.id}`}
                onClick={(e) => e.stopPropagation()}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit Supplier">
              <IconButton
                size="small"
                color="primary"
                component={RouterLink}
                to={`/suppliers/edit/${supplier.id}`}
                onClick={(e) => e.stopPropagation()}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>

      {/* Expandable parts section */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" gutterBottom component="div">
                  Parts Supplied
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  component={RouterLink}
                  to={`/suppliers/${supplier.id}/add-part`}
                >
                  Add Part
                </Button>
              </Box>

              {loading ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  Loading parts...
                </Typography>
              ) : parts.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No parts associated with this supplier yet.
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Part Number</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Supplier Part #</TableCell>
                        <TableCell align="right">Cost</TableCell>
                        <TableCell align="right">Lead Time</TableCell>
                        <TableCell>URL</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parts.map((part) => (
                        <TableRow key={part.id} hover>
                          <TableCell>
                            <RouterLink
                              to={`/inventory/${part.part_id}`}
                              style={{
                                color: 'inherit',
                                textDecoration: 'none',
                                fontWeight: 500
                              }}
                            >
                              {part.part?.internal_part_number || 'N/A'}
                            </RouterLink>
                          </TableCell>
                          <TableCell>{part.part?.description || 'N/A'}</TableCell>
                          <TableCell>{part.supplier_part_number}</TableCell>
                          <TableCell align="right">${parseFloat(part.cost).toFixed(2)}</TableCell>
                          <TableCell align="right">{part.lead_time_days || 'N/A'}</TableCell>
                          <TableCell>
                            {part.url ? (
                              <Link
                                href={part.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ display: 'flex', alignItems: 'center' }}
                              >
                                <LinkIcon fontSize="small" sx={{ mr: 0.5 }} />
                                View
                              </Link>
                            ) : (
                              'N/A'
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="View Part Details">
                              <IconButton
                                size="small"
                                component={RouterLink}
                                to={`/inventory/${part.part_id}`}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// Main component
const ExpandableSupplierGrid = ({ suppliers, onSearchChange, initialSearchValue = '' }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [filterText, setFilterText] = useState(initialSearchValue);
  const [supplierPartsCount, setSupplierPartsCount] = useState({});

  // Initialize parts count from server data when suppliers change
  useEffect(() => {
    if (suppliers && suppliers.length > 0) {
      const initialCounts = {};
      suppliers.forEach(supplier => {
        // Use parts_count from server if available, otherwise keep existing count or default to 0
        if (supplier.parts_count !== undefined) {
          initialCounts[supplier.id] = supplier.parts_count;
        }
      });

      // Only update if we have new counts to avoid unnecessary re-renders
      if (Object.keys(initialCounts).length > 0) {
        setSupplierPartsCount(prev => ({
          ...prev,
          ...initialCounts
        }));
      }
    }
  }, [suppliers]);

  // Handle sorting
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Handle pagination
  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle filtering - immediately update UI and notify parent
  const handleFilterChange = (event) => {
    const newValue = event.target.value;
    setFilterText(newValue);
    setPage(0);

    // Call the parent component's search handler immediately
    // The parent will handle debouncing
    if (onSearchChange) {
      onSearchChange(newValue);
    }
  };

  // Handle parts loading for a supplier
  const handlePartsLoad = (supplierId, parts) => {
    setSupplierPartsCount(prev => ({
      ...prev,
      [supplierId]: parts.length
    }));
  };

  // Note: In a future update, this will be replaced with server-side filtering
  // For now, we're still using client-side filtering
  const filteredData = suppliers.filter((supplier) => {
    const searchText = filterText.toLowerCase();
    return (
      supplier.name.toLowerCase().includes(searchText) ||
      (supplier.contact_person && supplier.contact_person.toLowerCase().includes(searchText)) ||
      (supplier.email && supplier.email.toLowerCase().includes(searchText)) ||
      (supplier.phone && supplier.phone.toLowerCase().includes(searchText))
    );
  });

  const sortedData = filteredData.sort((a, b) => {
    const aValue = a[orderBy] || '';
    const bValue = b[orderBy] || '';

    if (order === 'asc') {
      return aValue.localeCompare(bValue);
    } else {
      return bValue.localeCompare(aValue);
    }
  });

  // Paginate data
  const paginatedData = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Enhance suppliers with parts count
  const enhancedSuppliers = paginatedData.map(supplier => ({
    ...supplier,
    parts_count: supplierPartsCount[supplier.id] || 0
  }));

  return (
    <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
      {/* Toolbar with title, filter, and add button */}
      <Toolbar
        sx={{
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
          bgcolor: 'background.paper',
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`
        }}
      >
        <Typography
          sx={{ flex: '1 1 100%' }}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          Suppliers
        </Typography>

        <TextField
          size="small"
          variant="outlined"
          placeholder="Filter suppliers..."
          value={filterText}
          onChange={handleFilterChange}
          sx={{ mr: 2, width: 250 }}
          // Using startAdornment directly instead of deprecated InputProps
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          }
        />

        <Tooltip title="Add New Supplier">
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            component={RouterLink}
            to="/suppliers/new"
            size="small"
          >
            Add Supplier
          </Button>
        </Tooltip>
      </Toolbar>

      {/* Table */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleRequestSort('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'contact_person'}
                  direction={orderBy === 'contact_person' ? order : 'asc'}
                  onClick={() => handleRequestSort('contact_person')}
                >
                  Contact Person
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'email'}
                  direction={orderBy === 'email' ? order : 'asc'}
                  onClick={() => handleRequestSort('email')}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'phone'}
                  direction={orderBy === 'phone' ? order : 'asc'}
                  onClick={() => handleRequestSort('phone')}
                >
                  Phone
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">Parts</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {enhancedSuppliers.length > 0 ? (
              enhancedSuppliers.map((supplier) => (
                <SupplierRow
                  key={supplier.id}
                  supplier={supplier}
                  onPartsLoad={handlePartsLoad}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  {filterText ? (
                    <Typography variant="body2" color="text.secondary">
                      No suppliers match the filter criteria
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No suppliers found. Click "Add Supplier" to create one.
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default ExpandableSupplierGrid;
